import { createContext, useCallback, useRef, useEffect, useState, ReactNode } from 'react';
import Shepherd from 'shepherd.js';
import type { StepOptions, Tour } from 'shepherd.js';

export interface TourGuide {
  id: string;
  title: string;
  description: string;
  category: 'getting-started' | 'daily-operations' | 'administration' | 'advanced';
  steps: StepOptions[];
  estimatedMinutes?: number;
}

interface TourProgress {
  [guideId: string]: {
    completed: boolean;
    lastSeen?: string;
    completionCount: number;
  };
}

interface TourContextType {
  tourRef: React.MutableRefObject<Tour | null>;
  startTour: (guideId: string) => void;
  stopTour: () => void;
  isActive: () => boolean;
  registerTourGuide: (guide: TourGuide) => void;
  tourGuides: Map<string, TourGuide>;
  currentGuideId: string | null;
  tourProgress: TourProgress;
  markTourComplete: (guideId: string) => void;
  resetTourProgress: () => void;
  getCompletionPercentage: () => number;
}

export const TourContext = createContext<TourContextType | undefined>(undefined);

const TOUR_PROGRESS_KEY = 'clinic-tour-progress-v2';

const createDefaultTourGuides = (): Map<string, TourGuide> => {
  const guides = new Map<string, TourGuide>();

  // Getting Started Category
  guides.set('welcome-onboarding', {
    id: 'welcome-onboarding',
    title: '🎉 Welcome to Med OPD',
    description: 'Get started with your clinic management system',
    category: 'getting-started',
    estimatedMinutes: 3,
    steps: [
      {
        id: 'welcome',
        title: '👋 Welcome!',
        text: `<p>Welcome to Med OPD - your complete clinic management solution!</p>
               <p>This quick tour will help you understand the key features.</p>`,
        buttons: [
          { text: 'Skip Tour', classes: 'shepherd-button-secondary', action() { this.cancel(); } },
          { text: 'Start Tour', action() { this.next(); } },
        ],
      },
      {
        id: 'dashboard-intro',
        title: '📊 Dashboard',
        text: 'Your dashboard shows real-time clinic metrics, appointments, and patient queue.',
        attachTo: { element: '[data-tour="nav-dashboard"]', on: 'right' },
      },
      {
        id: 'appointments-intro',
        title: '📅 Appointments',
        text: 'Schedule, manage, and track patient appointments with token numbers.',
        attachTo: { element: '[data-tour="nav-appointments"]', on: 'right' },
      },
      {
        id: 'patients-intro',
        title: '👥 Patients',
        text: 'Manage patient profiles, medical history, and records.',
        attachTo: { element: '[data-tour="nav-patients"]', on: 'right' },
      },
      {
        id: 'help-menu',
        title: '❓ Need Help?',
        text: 'Access contextual help guides anytime by clicking the help button in the header.',
        attachTo: { element: '[data-tour="help-menu"]', on: 'bottom' },
      },
    ],
  });

  guides.set('profile-security', {
    id: 'profile-security',
    title: '🔐 Profile & Security',
    description: 'Manage your profile and security settings',
    category: 'getting-started',
    estimatedMinutes: 4,
    steps: [
      {
        id: 'profile-menu',
        title: '👤 Your Profile',
        text: 'Click here to access your profile, settings, and security options.',
        attachTo: { element: '[data-tour="profile-menu"]', on: 'bottom' },
      },
      {
        id: 'profile-avatar',
        title: '🖼️ Profile Picture',
        text: 'Upload or update your profile picture. Click the avatar to change it.',
        attachTo: { element: '[data-tour="profile-avatar"]', on: 'bottom' },
      },
      {
        id: 'security-settings',
        title: '🔒 Security Settings',
        text: 'Configure app lock with PIN protection for enhanced security.',
        attachTo: { element: '[data-tour="security-settings"]', on: 'left' },
      },
      {
        id: 'pin-setup',
        title: '📱 Setup PIN',
        text: 'Set up a 6-digit PIN to automatically lock the app after inactivity.',
        attachTo: { element: '[data-tour="setup-pin-button"]', on: 'top' },
      },
      {
        id: 'auto-lock-timeout',
        title: '⏱️ Auto-Lock Timeout',
        text: 'Choose when the app should lock automatically - from 1 to 10 minutes of inactivity.',
        attachTo: { element: '[data-tour="auto-lock-timeout"]', on: 'top' },
      },
    ],
  });

  // Daily Operations Category
  guides.set('queue-management', {
    id: 'queue-management',
    title: '🎫 Queue Management',
    description: 'Manage patient queue and token system',
    category: 'daily-operations',
    estimatedMinutes: 3,
    steps: [
      {
        id: 'queue-intro',
        title: '🎫 Token System',
        text: 'Every appointment gets a unique token number for queue management.',
        attachTo: { element: '[data-tour="queue-section"]', on: 'top' },
      },
      {
        id: 'call-next',
        title: '📢 Call Next Patient',
        text: 'Click here to call the next patient in the queue and update their status.',
        attachTo: { element: '[data-tour="call-next-button"]', on: 'left' },
      },
      {
        id: 'queue-display',
        title: '📺 Queue Display',
        text: 'Display the current queue on a TV screen for waiting patients.',
        attachTo: { element: '[data-tour="queue-display-button"]', on: 'top' },
      },
      {
        id: 'queue-stats',
        title: '📊 Queue Statistics',
        text: 'Track average wait time and queue length in real-time.',
        attachTo: { element: '[data-tour="queue-stats"]', on: 'bottom' },
      },
    ],
  });

  guides.set('appointments-detailed', {
    id: 'appointments-detailed',
    title: '📅 Appointment Management',
    description: 'Complete guide to scheduling and managing appointments',
    category: 'daily-operations',
    estimatedMinutes: 5,
    steps: [
      {
        id: 'appointments-nav',
        title: '📅 Appointments Screen',
        text: 'Access appointments to view, create, and manage patient bookings.',
        attachTo: { element: '[data-tour="nav-appointments"]', on: 'right' },
      },
      {
        id: 'create-appointment',
        title: '➕ Create Appointment',
        text: 'Click here to schedule a new appointment with patient details.',
        attachTo: { element: '[data-tour="create-appointment-button"]', on: 'bottom' },
      },
      {
        id: 'appointment-calendar',
        title: '📆 Calendar View',
        text: 'Switch between list and calendar views for better scheduling.',
        attachTo: { element: '[data-tour="calendar-toggle"]', on: 'bottom' },
      },
      {
        id: 'filter-appointments',
        title: '🔍 Filter Options',
        text: 'Filter appointments by doctor, date, or status to find what you need.',
        attachTo: { element: '[data-tour="appointment-filters"]', on: 'left' },
      },
      {
        id: 'appointment-status',
        title: '✅ Status Updates',
        text: 'Update appointment status: Scheduled → Checked-in → In-Progress → Completed.',
        attachTo: { element: '[data-tour="status-buttons"]', on: 'top' },
      },
      {
        id: 'concurrent-booking',
        title: '⚠️ Concurrent Booking Protection',
        text: 'The system prevents double-booking and manages slot conflicts automatically.',
      },
    ],
  });

  guides.set('patients-detailed', {
    id: 'patients-detailed',
    title: '👥 Patient Management',
    description: 'Managing patient profiles and medical records',
    category: 'daily-operations',
    estimatedMinutes: 4,
    steps: [
      {
        id: 'patients-nav',
        title: '👥 Patients Screen',
        text: 'Access the patients section to manage profiles and medical records.',
        attachTo: { element: '[data-tour="nav-patients"]', on: 'right' },
      },
      {
        id: 'search-patients',
        title: '🔍 Quick Search',
        text: 'Search patients by name, phone number, or patient ID.',
        attachTo: { element: '[data-tour="patients-search"]', on: 'bottom' },
      },
      {
        id: 'add-patient',
        title: '➕ Add New Patient',
        text: 'Register new patients with their personal and medical information.',
        attachTo: { element: '[data-tour="add-patient-button"]', on: 'bottom' },
      },
      {
        id: 'patient-details',
        title: '📋 Patient Profile',
        text: 'View complete patient history including visits, prescriptions, and vitals.',
        attachTo: { element: '[data-tour="patient-profile"]', on: 'left' },
      },
      {
        id: 'patient-vitals',
        title: '🩺 Vitals Tracking',
        text: 'Record and track patient vitals like blood pressure, temperature, and weight.',
        attachTo: { element: '[data-tour="vitals-section"]', on: 'top' },
      },
      {
        id: 'patient-invite',
        title: '✉️ Send Portal Invite',
        text: 'Invite patients to access their records through the patient portal.',
        attachTo: { element: '[data-tour="invite-patient-button"]', on: 'top' },
      },
    ],
  });

  guides.set('clinical-features', {
    id: 'clinical-features',
    title: '🩺 Clinical Features',
    description: 'Prescriptions, notes, and medical records',
    category: 'daily-operations',
    estimatedMinutes: 5,
    steps: [
      {
        id: 'prescriptions-intro',
        title: '💊 Prescriptions',
        text: 'Create and manage patient prescriptions with medication details.',
        attachTo: { element: '[data-tour="prescriptions-tab"]', on: 'top' },
      },
      {
        id: 'create-prescription',
        title: '➕ Add Medication',
        text: 'Add medications with dosage, frequency, and duration.',
        attachTo: { element: '[data-tour="add-prescription-button"]', on: 'bottom' },
      },
      {
        id: 'clinical-notes',
        title: '📝 Clinical Notes',
        text: 'Document patient consultations, symptoms, and treatment plans.',
        attachTo: { element: '[data-tour="clinical-notes-tab"]', on: 'top' },
      },
      {
        id: 'lab-results',
        title: '🧪 Lab Results',
        text: 'Track blood tests, urine analysis, and other lab work.',
        attachTo: { element: '[data-tour="lab-results-section"]', on: 'left' },
      },
      {
        id: 'visit-summary',
        title: '📄 Visit Summary',
        text: 'Generate comprehensive visit summaries with all clinical data.',
        attachTo: { element: '[data-tour="visit-summary-button"]', on: 'top' },
      },
    ],
  });

  // Administration Category
  guides.set('billing-detailed', {
    id: 'billing-detailed',
    title: '💰 Billing & Payments',
    description: 'Invoice generation and payment tracking',
    category: 'administration',
    estimatedMinutes: 4,
    steps: [
      {
        id: 'billing-nav',
        title: '💰 Billing Section',
        text: 'Manage invoices, payments, and financial records.',
        attachTo: { element: '[data-tour="nav-billing"]', on: 'right' },
      },
      {
        id: 'generate-bill',
        title: '📄 Generate Invoice',
        text: 'Create itemized invoices with services, procedures, and medications.',
        attachTo: { element: '[data-tour="generate-bill-button"]', on: 'bottom' },
      },
      {
        id: 'bill-items',
        title: '📋 Invoice Items',
        text: 'Add consultation fees, procedures, and medicines with individual pricing.',
        attachTo: { element: '[data-tour="bill-items-section"]', on: 'left' },
      },
      {
        id: 'payment-mode',
        title: '💳 Payment Methods',
        text: 'Accept payments via Cash, Card, UPI, or Insurance.',
        attachTo: { element: '[data-tour="payment-mode-select"]', on: 'top' },
      },
      {
        id: 'gst-calculation',
        title: '🧮 GST Calculation',
        text: 'GST is automatically calculated based on your clinic configuration.',
        attachTo: { element: '[data-tour="gst-section"]', on: 'top' },
      },
      {
        id: 'print-bill',
        title: '🖨️ Print Invoice',
        text: 'Generate professional invoices with clinic letterhead.',
        attachTo: { element: '[data-tour="print-bill-button"]', on: 'top' },
      },
    ],
  });

  guides.set('staff-management-detailed', {
    id: 'staff-management-detailed',
    title: '👨‍⚕️ Staff Management',
    description: 'Invite and manage clinic staff members',
    category: 'administration',
    estimatedMinutes: 3,
    steps: [
      {
        id: 'staff-nav',
        title: '👨‍⚕️ Staff Management',
        text: 'Manage doctors, receptionists, and other clinic staff.',
        attachTo: { element: '[data-tour="nav-staff"]', on: 'right' },
      },
      {
        id: 'invite-staff',
        title: '✉️ Invite Staff Member',
        text: 'Send invitation emails with secure signup links.',
        attachTo: { element: '[data-tour="invite-staff-button"]', on: 'bottom' },
      },
      {
        id: 'staff-roles',
        title: '🎭 Role Assignment',
        text: 'Assign roles: Clinic Admin, Doctor, or Staff with specific permissions.',
        attachTo: { element: '[data-tour="role-select"]', on: 'top' },
      },
      {
        id: 'staff-status',
        title: '✅ Staff Status',
        text: 'Activate or deactivate staff access as needed.',
        attachTo: { element: '[data-tour="staff-status-toggle"]', on: 'left' },
      },
      {
        id: 'pending-invitations',
        title: '📧 Pending Invitations',
        text: 'Track and resend invitation emails to staff members.',
        attachTo: { element: '[data-tour="pending-invitations"]', on: 'top' },
      },
    ],
  });

  guides.set('inventory-management', {
    id: 'inventory-management',
    title: '📦 Inventory Management',
    description: 'Track medicines and supplies',
    category: 'administration',
    estimatedMinutes: 4,
    steps: [
      {
        id: 'inventory-intro',
        title: '📦 Stock Management',
        text: 'Track medicines, supplies, and equipment inventory.',
        attachTo: { element: '[data-tour="nav-inventory"]', on: 'right' },
      },
      {
        id: 'add-item',
        title: '➕ Add Inventory Item',
        text: 'Add new medicines or supplies with quantity and expiry dates.',
        attachTo: { element: '[data-tour="add-inventory-button"]', on: 'bottom' },
      },
      {
        id: 'low-stock-alerts',
        title: '⚠️ Low Stock Alerts',
        text: 'Get notified when items fall below minimum stock levels.',
        attachTo: { element: '[data-tour="low-stock-alerts"]', on: 'left' },
      },
      {
        id: 'expiry-tracking',
        title: '⏰ Expiry Tracking',
        text: 'Track expiration dates and get alerts for items nearing expiry.',
        attachTo: { element: '[data-tour="expiry-warnings"]', on: 'top' },
      },
      {
        id: 'inventory-config',
        title: '⚙️ Configuration',
        text: 'Set minimum stock levels and notification preferences.',
        attachTo: { element: '[data-tour="inventory-config-button"]', on: 'top' },
      },
    ],
  });

  // Advanced Category
  guides.set('analytics-dashboard', {
    id: 'analytics-dashboard',
    title: '📊 Analytics & Reports',
    description: 'Insights into clinic performance',
    category: 'advanced',
    estimatedMinutes: 5,
    steps: [
      {
        id: 'analytics-intro',
        title: '📈 Analytics Dashboard',
        text: 'Visualize clinic performance with charts and metrics.',
        attachTo: { element: '[data-tour="nav-analytics"]', on: 'right' },
      },
      {
        id: 'revenue-chart',
        title: '💹 Revenue Analytics',
        text: 'Track daily, weekly, and monthly revenue trends.',
        attachTo: { element: '[data-tour="revenue-chart"]', on: 'bottom' },
      },
      {
        id: 'patient-trends',
        title: '👥 Patient Trends',
        text: 'Analyze new vs returning patients and appointment patterns.',
        attachTo: { element: '[data-tour="patient-trends"]', on: 'bottom' },
      },
      {
        id: 'doctor-performance',
        title: '⚕️ Doctor Performance',
        text: 'Compare patient count and revenue per doctor.',
        attachTo: { element: '[data-tour="doctor-stats"]', on: 'left' },
      },
      {
        id: 'date-range',
        title: '📅 Custom Date Range',
        text: 'Select custom date ranges for detailed analysis.',
        attachTo: { element: '[data-tour="date-range-picker"]', on: 'top' },
      },
      {
        id: 'export-reports',
        title: '📥 Export Reports',
        text: 'Download reports as Excel or PDF for external analysis.',
        attachTo: { element: '[data-tour="export-button"]', on: 'top' },
      },
    ],
  });

  guides.set('chatbot-assistant', {
    id: 'chatbot-assistant',
    title: '🤖 AI Chatbot Assistant',
    description: 'Get AI-powered help and insights',
    category: 'advanced',
    estimatedMinutes: 3,
    steps: [
      {
        id: 'chatbot-intro',
        title: '🤖 AI Assistant',
        text: 'Chat with our AI assistant powered by AWS Bedrock for medical queries.',
        attachTo: { element: '[data-tour="chatbot-button"]', on: 'left' },
      },
      {
        id: 'start-conversation',
        title: '💬 Start Conversation',
        text: 'Click here to open a new chat session with the assistant.',
        attachTo: { element: '[data-tour="new-chat-button"]', on: 'bottom' },
      },
      {
        id: 'ask-questions',
        title: '❓ Ask Questions',
        text: 'Ask about symptoms, medications, procedures, or general medical information.',
        attachTo: { element: '[data-tour="chat-input"]', on: 'top' },
      },
      {
        id: 'conversation-history',
        title: '📚 Chat History',
        text: 'Access previous conversations and continue where you left off.',
        attachTo: { element: '[data-tour="chat-history"]', on: 'left' },
      },
      {
        id: 'context-aware',
        title: '🧠 Context-Aware',
        text: 'The assistant remembers conversation context for more relevant responses.',
      },
    ],
  });

  guides.set('reports-generation', {
    id: 'reports-generation',
    title: '📄 Reports & Exports',
    description: 'Generate and export clinic reports',
    category: 'advanced',
    estimatedMinutes: 3,
    steps: [
      {
        id: 'reports-nav',
        title: '📄 Reports Section',
        text: 'Generate comprehensive reports for clinic operations.',
        attachTo: { element: '[data-tour="nav-reports"]', on: 'right' },
      },
      {
        id: 'daily-report',
        title: '📅 Daily Reports',
        text: 'View daily summary of appointments, revenue, and patient visits.',
        attachTo: { element: '[data-tour="daily-report-card"]', on: 'bottom' },
      },
      {
        id: 'custom-reports',
        title: '🔧 Custom Reports',
        text: 'Create custom reports with specific date ranges and filters.',
        attachTo: { element: '[data-tour="custom-report-builder"]', on: 'left' },
      },
      {
        id: 'audit-logs',
        title: '🔍 Audit Logs',
        text: 'Track all system activities and user actions for compliance.',
        attachTo: { element: '[data-tour="audit-logs-button"]', on: 'top' },
      },
    ],
  });

  // Clinical Operations
  guides.set('inpatient-management', {
    id: 'inpatient-management',
    title: '🏥 Advanced Inpatient Management',
    description: 'Master the high-density clinical workflow for hospitalized patients',
    category: 'daily-operations',
    estimatedMinutes: 6,
    steps: [
      {
        id: 'inpatient-intro',
        title: '🏥 Inpatient Central Command',
        text: 'Welcome to the Inpatient Census. This high-density module is designed for rapid clinical assessment and ward management. Here, you can track every admitted patient across all wards, monitor their conditions in real-time, and perform critical clinical interventions.',
        attachTo: { element: '[data-tour="inpatient-header"]', on: 'bottom' },
      },
      {
        id: 'inpatient-search',
        title: '🔍 Omni-Search Capability',
        text: 'This intelligent search bar allows you to locate any patient across the entire hospital instantly. You can search by Name, Bed Number, or Ward. When you type here, the system automatically bypasses individual ward filters to provide a global hospital view, ensuring no time is lost during emergencies.',
        attachTo: { element: '[data-tour="inpatient-search"]', on: 'bottom' },
      },
      {
        id: 'inpatient-admit',
        title: '🆕 Admission Control',
        text: 'Use the "Admit Patient" action to transition a patient from Outpatient/Emergency status to a formal Ward Admission. This workflow handles bed assignment, initial vitals, and primary diagnosis entry to establish the clinical baseline.',
        attachTo: { element: '[data-tour="inpatient-actions"]', on: 'bottom' },
      },
      {
        id: 'inpatient-navigation',
        title: '📊 Ward & Bed Navigation',
        text: 'Quickly toggle between different hospital zones like General Ward, ICU, or Semi-Private rooms. Each tab provides a filtered view of patients in that specific area. The "Beds" tab offers a structural view for Bed Management and Maintenance.',
        attachTo: { element: '[data-tour="inpatient-tabs"]', on: 'bottom' },
      },
      {
        id: 'inpatient-card-overview',
        title: '📋 Clinical Patient Profile',
        text: 'Each card represents a live admission. It consolidates patient demographics, current clinical status, and location within the ward. The card is color-coded by the patient\'s severity level for immediate visual triage.',
        attachTo: { element: '[data-tour="inpatient-card"]', on: 'bottom' },
      },
      {
        id: 'inpatient-triage',
        title: '🚦 Triage & Condition Monitoring',
        text: 'The vertical color bar indicates the current condition: Green for Stable, Yellow for Serious, and Red for Critical. This allows nursing staff and doctors to prioritize assessments during shift changes or ward rounds.',
        attachTo: { element: '[data-tour="inpatient-status"]', on: 'right' },
      },
      {
        id: 'inpatient-ai-alerts',
        title: '🤖 AI Smart Alerts',
        text: 'The Clinical Alerts badge is powered by our AI engine. It continuously monitors vitals and lab results to identify abnormal patterns or deterioration risks. Clicking this provides a deep-dive into active clinical warnings.',
        attachTo: { element: '[data-tour="inpatient-alerts"]', on: 'top' },
      },
      {
        id: 'inpatient-rounds',
        title: '👨‍⚕️ Strategic Ward Rounds',
        text: 'The "Round" button initiates the clinical documentation workflow. During a round, you record current vitals, update the patient\'s condition, and modify the treatment plan. This data is instantly synced to the nursing station.',
        attachTo: { element: '[data-tour="inpatient-round-btn"]', on: 'top' },
      },
      {
        id: 'inpatient-ai-analysis',
        title: '✨ AI Clinical Analysis',
        text: 'Click the Sparkle icon to trigger a Comprehensive AI Clinical Analysis. The system aggregates all historical data, current vitals, and lab reports to suggest potential diagnoses or clinical risks using advanced medical reasoning models.',
        attachTo: { element: '[data-tour="inpatient-ai-btn"]', on: 'top' },
      },
      {
        id: 'inpatient-history-dashboard',
        title: '📈 Clinical History & Dashboard',
        text: 'Access chronological records through the History icon, or use the Clinical Dashboard for a high-level visual summary of the entire admission journey, including lab trends and medication adherence.',
        attachTo: { element: '[data-tour="inpatient-dashboard-btn"]', on: 'top' },
      },
    ],
  });

  return guides;
};

export function TourProvider({ children }: { children: ReactNode }) {
  const tourRef = useRef<Tour | null>(null);
  const [tourGuides] = useState<Map<string, TourGuide>>(() => createDefaultTourGuides());
  const [currentGuideId, setCurrentGuideId] = useState<string | null>(null);
  const [tourProgress, setTourProgress] = useState<TourProgress>(() => {
    const stored = localStorage.getItem(TOUR_PROGRESS_KEY);
    return stored ? JSON.parse(stored) : {};
  });

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(TOUR_PROGRESS_KEY, JSON.stringify(tourProgress));
  }, [tourProgress]);

  const registerTourGuide = useCallback((guide: TourGuide) => {
    tourGuides.set(guide.id, guide);
  }, [tourGuides]);

  const markTourComplete = useCallback((guideId: string) => {
    setTourProgress(prev => ({
      ...prev,
      [guideId]: {
        completed: true,
        lastSeen: new Date().toISOString(),
        completionCount: (prev[guideId]?.completionCount || 0) + 1,
      },
    }));
  }, []);

  const resetTourProgress = useCallback(() => {
    setTourProgress({});
    localStorage.removeItem(TOUR_PROGRESS_KEY);
  }, []);

  const getCompletionPercentage = useCallback(() => {
    const totalTours = tourGuides.size;
    const completedTours = Object.values(tourProgress).filter(p => p.completed).length;
    return totalTours > 0 ? Math.round((completedTours / totalTours) * 100) : 0;
  }, [tourGuides, tourProgress]);

  const startTour = useCallback((guideId: string) => {
    const guide = tourGuides.get(guideId);
    if (!guide) {
      console.warn(`Tour guide "${guideId}" not found`);
      return;
    }

    if (!tourRef.current) {
      tourRef.current = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          cancelIcon: { enabled: true },
          scrollTo: { behavior: 'smooth', block: 'center' },
          modalOverlayOpeningPadding: 8,
          modalOverlayOpeningRadius: 4,
        },
      });

      const handleFinish = () => {
        setCurrentGuideId(null);
        markTourComplete(guideId);
      };

      tourRef.current.on('complete', handleFinish);
      tourRef.current.on('cancel', () => {
        setCurrentGuideId(null);
      });
    }

    const tour = tourRef.current;

    // Clear existing steps
    const existingSteps = tour.steps ?? [];
    existingSteps.forEach((step) => {
      if (step.id) {
        tour.removeStep(step.id);
      }
    });

    // Add new steps with enhanced buttons
    guide.steps.forEach((step, index) => {
      const isFirst = index === 0;
      const isLast = index === guide.steps.length - 1;
      
      tour.addStep({
        ...step,
        when: {
          show() {
            // Scroll element into view if it exists
            const element = step.attachTo?.element;
            if (typeof element === 'string') {
              const elem = document.querySelector(element);
              if (elem) {
                elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          },
        },
        buttons: isLast
          ? [
              {
                text: 'Back',
                classes: 'shepherd-button-secondary',
                action: () => tour.back(),
              },
              {
                text: '✅ Finish Tour',
                action: () => tour.complete(),
              },
            ]
          : isFirst
          ? [
              {
                text: 'Skip Tour',
                classes: 'shepherd-button-secondary',
                action: () => tour.cancel(),
              },
              {
                text: 'Next →',
                action: () => tour.next(),
              },
            ]
          : [
              {
                text: '← Back',
                classes: 'shepherd-button-secondary',
                action: () => tour.back(),
              },
              {
                text: 'Skip',
                classes: 'shepherd-button-secondary',
                action: () => tour.cancel(),
              },
              {
                text: 'Next →',
                action: () => tour.next(),
              },
            ],
      });
    });

    setCurrentGuideId(guideId);
    
    // START THE TOUR IMMEDIATELY WHEN TRIGGERED MANUALLY
    if (tour.isActive()) {
      tour.cancel();
    }
    
    // Short timeout to ensure elements are rendered if we just navigated
    window.setTimeout(() => {
      tour.start();
    }, 150);
  }, [tourGuides, markTourComplete]);

  const stopTour = useCallback(() => {
    if (tourRef.current?.isActive()) {
      tourRef.current.cancel();
    }
    setCurrentGuideId(null);
  }, []);

  const isActive = useCallback(() => {
    return tourRef.current?.isActive() ?? false;
  }, []);

  // Keyboard shortcut: Press ? to open tour menu
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        // Don't trigger if typing in input/textarea
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        // Dispatch custom event to open tour menu
        window.dispatchEvent(new CustomEvent('open-tour-menu'));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const value: TourContextType = {
    tourRef,
    startTour,
    stopTour,
    isActive,
    registerTourGuide,
    tourGuides,
    currentGuideId,
    tourProgress,
    markTourComplete,
    resetTourProgress,
    getCompletionPercentage,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
